// The SETTINGS panel of the hub (R229), in the look of the ☸ OPTIONS menu
// (R1731; card 20 of mockup round 081, ui-map-interface.md §16.14).
//
// **One structure, two shells.** The panel shows the sections of the game
// menu in the same order, with the same glyph, word and summary, and the
// same rows: MAP, AUDIO, INTERFACE, PLAY, ALERTS and SOCIAL. HELP is not
// here: photo mode, KEYS and the REPORT sheet are tools of a world, and the
// hub has its own SUPPORT section. The section shell of the hub draws it
// (`../sectionshell.ts`, ui-map-interface.md §16.16) in its SETTINGS form:
// a wide pane (`ctx.wide()`) draws a rail of the sections at the left and
// the rows of the open section at the right, inside the hub page, and the
// WHERE IT SHOWS column is its companion column. A narrow pane draws the
// sections as tiles, and a press opens one page a section with ‹ SETTINGS
// at its top. The hub draws no QUICK row and no session block: it plays no
// match, and MENU and ARCADE are ways out of a world.
//
// **The commander card opens the ACCOUNT page.** It stands under the
// sections, in the rail and under the tiles. The page is the account
// category of the settings (R621), and it is not part of the blob. It
// holds three sections, and **each section is one fold button** (R1563):
// the button carries the heading and `aria-expanded`, and the section shows
// its rows only after a press. The folds start closed every time the panel
// opens, except SIGN-IN METHODS for a guest: the REGISTER of the guest
// warning lands here, so a guest opens on the ACCOUNT page with that fold
// open (R1565). A THEMES link row under the folds opens THEMES of MODDING,
// the one place of the six theme categories (R807, themes.md §2).
//
// The first section is SIGN-IN METHODS, with one row for each of EMAIL,
// GOOGLE and APPLE. A row says LINKED when `Me.identities` holds that
// method, it offers LINK when this deployment offers the method, and it
// says NOT OFFERED YET when the deployment has no keys for the provider.
// The SETTINGS panel is the ONE place a player links a method: the landing
// screen signs in, and never links. **The second section is the password
// change** (R626), only for an account whose `identities` name `email`.
// **The third section is the account delete** (deployment.md §10.4):
// `erase.ts` draws it for every account, a guest included.
//
// **The address may name a page** (`?tab=settings&section=alerts`,
// `SECTION_PARAM`). The hub hands it to `openPage` before the first
// `open`, and the panel opens on that page. A link of a sign-in method
// comes back to the ACCOUNT page the same way.
//
// **Four rows write their own route and not the blob.** The advisor
// (R1566) and the standing orders (R1256) of PLAY belong to the account,
// because the meta server and a world act on them: each writes with its
// own PUT at the press, never through the debounced blob push. The stated
// hours (`hours.ts`, R1304) stand in PLAY only for an account in no clan.
// ALERTS (`alerts.ts`, R1301) writes `/api/push`, and CHAT (`blocks.ts`)
// writes `/api/blocks`. The game menu mounts the last two cards too.
//
// What it reads live: the settings store, through `loadSettings()`. The
// blob belongs to the ACCOUNT since R256. The meta server keeps one opaque
// JSON object per account, this browser keeps a copy, and `saveSettings`
// writes both. The server reads no field of it, so nothing here is a rule.
// The console-unit flags `chat`, `minimap` and `heat` are not here: the
// in-game rail is their one switch (R218).
//
// **How this panel reaches the sound director.** It does not build one.
// `main.ts` pushes a sink into `ui/audiochoices.ts` before the hub opens,
// and every write here goes back out through it. That keeps one owner of
// sound in the client.

import { oneOf } from '../../../util/oneof';
import { flushSettings, loadSettings, saveSettings, type GameSettings } from '../../settings';
import { applyToSound, audioChoices } from '../../audiochoices';
import {
  ApiError, CODE_ALREADY_LINKED, messageOf,
  authRegister, changePassword, getMe, getSignInMethods, putAdvisor, putLeave, putStandingOrders,
} from '../../../net/api';
import { LANGUAGES } from '../../../gen/balance';
import { startOAuth } from '../../../platform/signin';
import { fast as F, hubSettings as S, options as O } from '../../../gen/strings';
import { factionName } from '../../../world';
import { KEYS, OPTIONS_MENU } from '../../../gen/presentation';
import { matches } from '../../../keys';
import { badgePicture } from '../../chart/faction-key';
import { esc } from '../../esc';
import { button, pillGroup } from '../../choice/choice';
import { ABSENCE_COVER_AFTER_HOURS, LEAVE_DAYS } from '../../../gen/balance';
import { isoDateTime } from '../../../locale/stamp';
import {
  callout, commanderCard, linkRow, longLine,
  note, pageTitle, rootLabel, row, scopeMark, switchCtl, tag,
  type Commander,
} from '../../options/kit';
import {
  ACT, MARK_MODES, RANGE_KEYS, applyPress, audioRows, ifaceRows, mapRows, rangeStep, rangeValue, seaviewRows,
  socialRows, syncRangeMarks,
} from '../../options/rows';
import type { RangeKey } from '../../options/kit';
import { moveDeviceMarkScale } from '../../../arcade/markscale';
import { ACCOUNT_PAGE, pageOf, summaryOf, type SectionId, type SummaryFacts } from '../../options/sections';
import { whereColumn } from '../../options/where';
import { SectionNav, shellHtml, shellPress, type ShellSection } from '../sectionshell';
import {
  SETTINGS_PAGES, h1, textBox, wireOnce,
  type PageKey, type Panel, type PanelContext, type SettingsPage,
} from '../panel';
import { TTL_ON_OPEN, heldKeys } from '../hubstate';
import { stepUiTheme } from '../../options/themepick';
import { setThemePick } from '../../themestate';
import { SETTINGS_TYPED_CELL, createHoursCard, storedSettingsCell } from './hours';
import { createAlertsCard, envFlagRead } from './alerts';
import { createEraseSection } from './erase';
import { REAUTH_ACT, isReauth, reauthBlock, signInAgain, takeReauth } from './reauth';
import { blocksCell, createBlocksCard } from './blocks';
import { errNote, fold, okNote, runHeld, stateWord, type HeldCall } from './optcards';

/** The sections that hold stored choices, whose pane carries the legend of
 *  the changed dot and the scope marks. */
const LEGEND_PAGES: ReadonlySet<SettingsPage> = new Set(['map', 'audio', 'iface', 'play', 'alerts', 'social', 'seaview']);

/** The press of the shell: open one page, and go back to the tiles. */
const GO = 'set-page';
const BACK = 'set-back';
const SHELL = { go: GO, back: BACK };

/**
 * The three sign-in methods, in the order their rows stand in (R621).
 *
 * The name is the wire word `Me.identities` and `GET /api/auth/methods`
 * both use, so a row is matched by it and never by its label.
 */
const SIGN_IN_METHODS: [name: string, label: string, desc: string][] = [
  ['email', S.signin_email, S.signin_email_desc],
  ['google', S.signin_google, S.signin_google_desc],
  ['apple', S.signin_apple, S.signin_apple_desc],
];

/** What one sign-in row says on its right. */
type MethodState =
  /** The account holds this method. */
  | 'linked'
  /** The account does not hold it and this deployment offers it. */
  | 'link'
  /** This deployment has no keys for the provider. */
  | 'off'
  /** The method list has not answered yet. */
  | 'checking';

/** The three sections of the ACCOUNT page, in the order they stand in.
 *  Each one is a fold button (R1563). */
const ACCOUNT_FOLDS = ['signin', 'password', 'erase'] as const;
type AccountFold = (typeof ACCOUNT_FOLDS)[number];

/** The act of a fold button. */
const FOLD = 'acct-fold';

/** The fold that `key` names, or undefined for a key that names none. */
function foldOf(key: string | undefined): AccountFold | undefined {
  return oneOf(ACCOUNT_FOLDS, key);
}

/** True when `raw` is a language the LANGUAGE list offers. */
function langOffered(raw: string): boolean {
  return raw === 'auto' || LANGUAGES.some((l) => l.tag === raw);
}

/** One call of a form of the ACCOUNT page, as the page draws it: the
 *  call in flight and what it refused with, as a person reads it. */
type FormState = HeldCall;

export function createSettingsPanel(ctx: PanelContext): Panel {
  /** The flag set of the environment, as `/api/me` names it. */
  const hasFlag = envFlagRead(() => ctx.me());
  /** The blob, as this panel holds it. Every control renders from here and
   *  every write goes to the store and to the sink. */
  let s: GameSettings = loadSettings();
  /**
   * The blob the summary lines read. A drag of a volume writes `s` on every
   * step and leaves this copy alone until the drag ends: the AUDIO summary
   * names the levels, and a summary that moved with the drag would change
   * the markup, so the shell would rewrite the range under the finger.
   */
  let settled: GameSettings = s;

  /** The open page and the page the address named (R1731): the section
   *  tiles on a narrow pane, and the first section on a wide one. */
  const nav = new SectionNav({ ids: () => SETTINGS_PAGES, first: () => 'map' });
  /** The language the LANGUAGE list holds picked and not yet written: its
   *  confirm strip stands open (R855). */
  let pendingLang: string | null = null;

  /**
   * The step the last cue was played for, per slider.
   *
   * A drag raises an `input` event far more often than it crosses a step,
   * and a cue on every event is a rattle. −1 is "no cue has played".
   */
  const lastTick: Record<RangeKey, number> = {
    musicVol: -1, sfxVol: -1, seaIdleMin: -1, markScaleArcade: -1, markScaleCore: -1,
  };

  /** The sections of the ACCOUNT page that stand open (R1563). */
  const folds = new Set<AccountFold>();
  /** The fold button that takes the focus back after the next rewrite. A
   *  press sets it: the rewrite replaces the button, and a keyboard user
   *  must not lose the place. */
  let refocus: AccountFold | null = null;

  /** Whether the inline email form stands open under the EMAIL row. */
  let linkOpen = false;
  /** The register call of the inline email form. */
  const link: FormState = { busy: false, err: '' };
  /** The provider row whose start ticket failed, or '' (R2938). The row
   *  then says the sentence of a dead sign-in state. */
  let ticketFail = '';
  /** Whether the server asked for a fresh session (R2963): the email form
   *  or a link met `reauth_required`. The SIGN-IN METHODS section then
   *  shows SIGN IN AGAIN. */
  let reauth = false;
  /** The sign-out of SIGN IN AGAIN, while it runs, and its refusal. */
  const again: FormState = { busy: false, err: '' };
  /** What the boxes hold. The pane is rewritten whenever the markup
   *  changes, so the text lives here and the markup carries it back as a
   *  value, exactly as the CLAN panel's boxes do. */
  const text: Record<string, string> = {};

  /** The password change (R626). */
  const pw: FormState = { busy: false, err: '' };
  /** Whether the last password change went through. It is the page's one
   *  confirmation line, and a new attempt clears it. */
  let pwDone = false;

  /** The standing-orders switch as the last press left it, or undefined
   *  while the row still shows what the server answered (R1256). */
  let standing: boolean | undefined;
  /** The standing-orders write. */
  const so: FormState = { busy: false, err: '' };
  /** The advisor write (R1566). */
  const adv: FormState = { busy: false, err: '' };
  /** The end of the leave as the last press left it, or undefined while
   *  the row still shows what the server answered (R3283). */
  let leaveUntil: number | undefined;
  /** The leave write. */
  const lv: FormState = { busy: false, err: '' };

  /** The stated hours of the squad finder (player-reach.md §5.3, R1304). */
  const hours = createHoursCard(ctx, undefined, true);

  /** The ALERTS card (player-reach.md §3.5, R1301). Its BUZZ switches are
   *  keys of this panel's blob (R1613), so it writes through `write` and a
   *  later switch of the panel cannot write an old copy over them. A row
   *  behind a flag reads the flag set of the environment (R2656). */
  const alerts = createAlertsCard(ctx, undefined, undefined, {
    off: () => s.buzzOff,
    setOff: (off) => write({ ...s, buzzOff: off }),
  }, true, hasFlag);

  /** The account delete, the third section of the ACCOUNT page
   *  (deployment.md §10.4). */
  const erase = createEraseSection(ctx);

  /** The block list and LIFT (deployment.md §7.6). */
  const blocks = createBlocksCard(ctx, true);

  /** Writes the blob: the store first, then the director. A drag of a
   *  volume passes `settle` false, so the summaries keep their line. */
  function write(next: GameSettings, settle = true): void {
    s = next;
    if (settle) settled = next;
    saveSettings(s);
    applyToSound(s);
  }

  /** The page the pane shows: the open page, or on a wide pane the first
   *  section. Null is the tiles of a narrow pane. */
  function shown(): SettingsPage | null {
    return nav.shown(ctx.wide());
  }

  // --- the shell ------------------------------------------------------------

  /** What the summaries read that the blob does not hold. */
  function facts(): SummaryFacts {
    const read = blocksCell(ctx);
    return {
      sound: audioChoices().available,
      account: true,
      advisor: ctx.me().advisorOn,
      standing: standingNow(),
      blocked: read.state === 'ready' ? read.value?.blocks.length : undefined,
    };
  }

  /** The six sections as the rail and the tiles draw them. */
  function sections(): ShellSection[] {
    const f = facts();
    return SETTINGS_PAGES.filter((p): p is Exclude<SettingsPage, 'account'> => p !== 'account').map((id) => {
      const sec = pageOf(id);
      return { id, glyph: sec.glyph, name: sec.name, sum: summaryOf(id as SectionId, settled, f) };
    });
  }

  /** The commander. The row that opens the ACCOUNT page names no faction,
   *  because its badge shows it; the big card of the ACCOUNT page names it
   *  with `withFaction`. */
  function commander(withFaction = false): Commander {
    const me = ctx.me();
    const faction = me.account.faction ?? 0;
    return {
      name: me.account.name,
      guest: me.account.kind === 'guest',
      line: withFaction && faction > 0 ? factionName(faction) : '',
    };
  }

  /** The faction badge of the commander card, as the game menu draws it
   *  (R1766), at `px`. The hub holds no world and so no hold reading: the
   *  badge takes the modern reading, the one a world opens with. With no
   *  faction it is '', and the round mark stands in its place. */
  function badge(px: number): string {
    const faction = ctx.me().account.faction ?? 0;
    return faction > 0 ? badgePicture(faction, 'm', px) : '';
  }

  /** The title of one page. The ACCOUNT page names what it holds in the
   *  hub, which is not what the game menu's ACCOUNT page holds. */
  function title(id: SettingsPage, big: boolean): string {
    const sec = id === 'account' ? ACCOUNT_PAGE : pageOf(id);
    const what = id === 'account' ? S.account_what : sec.what;
    return pageTitle({ glyph: sec.glyph, name: sec.name, what, big, heading: true });
  }

  /** The legend at the foot of the wide pane (card 20). The hub holds no
   *  row of an empire, so the legend names the two marks it draws. */
  function legend(): string {
    return `<span class="o-lg"><span class="o-dot"></span>${esc(O.legend_changed)}</span>`
      + `<span class="o-lg"><span class="o-nomark">${esc(O.legend_nomark)}</span>${esc(O.legend_account)}</span>`
      + `<span class="o-lg">${scopeMark('device')}${esc(O.legend_device)}</span>`;
  }

  /** The pane in the section shell (§16.16), in its SETTINGS form: on a
   *  wide pane the rail with the commander under the sections, the rows
   *  of the open page and the legend at its foot, and beside the box the
   *  WHERE IT SHOWS column of the open page (R1762), which the stylesheet
   *  shows only while the pane has room for it next to the 980 px box; on
   *  a narrow pane the tiles with the commander under them, or one page
   *  under ‹ SETTINGS. The hub page has no ✕ and no BACK TO THE MAP bar. */
  function shell(): string {
    const wide = ctx.wide();
    const open = shown();
    const id = open ?? 'map';
    const press = { act: GO, id: 'account' };
    const badgeMark = badge(OPTIONS_MENU.commanderBadgePx);
    return shellHtml({
      title: S.title,
      heading: h1(S.title, S.subtitle),
      go: GO,
      back: BACK,
      sections: sections(),
      open,
      wide,
      form: true,
      railFoot: '<span class="hs-sep"></span>'
        + commanderCard(commander(), { press, on: id === 'account', badge: badgeMark }),
      rootHead: rootLabel(O.sections),
      rootFoot: rootLabel(O.sec_account) + commanderCard(commander(), { press, badge: badgeMark }),
      page: {
        title: title(id, !wide),
        body: body(id),
        foot: wide && LEGEND_PAGES.has(id) ? `<div class="o-pfoot">${legend()}</div>` : undefined,
        companion: wide ? { label: '', html: whereColumn(id), fold: 'none' } : undefined,
      },
    });
  }

  /** The rows of one page. */
  function body(id: SettingsPage): string {
    switch (id) {
      case 'map':
        return mapRows(s);
      case 'audio':
        // A host with no sound keeps the rows, so a choice made here takes
        // effect on a host that publishes a bank.
        return (audioChoices().available ? '' : note(S.sound_absent, true))
          + audioRows(s, { sound: true }) + note(O.vol_note);
      case 'iface':
        return ifaceRows(s, { pendingLang, held: heldKeys(ctx.me()), marks: MARK_MODES });
      case 'play':
        return advisorRow() + standingRow() + leaveRow() + hours.html();
      case 'alerts':
        return alerts.html();
      case 'social':
        // The two count switches, then the block list (R1759).
        return socialRows(s) + blocks.html();
      case 'seaview':
        return seaviewRows(s);
      case 'account':
        return accountBody();
    }
  }

  /** A page opened, or the tiles (null): the confirm strip of the
   *  language closes, and leaving the ACCOUNT page puts the delete at
   *  rest. */
  function moved(to: SettingsPage | null, from: SettingsPage | null): void {
    if (to === null || from === 'account') erase.clear();
    pendingLang = null;
  }

  // --- PLAY -----------------------------------------------------------------

  /** What the standing-orders switch reads now, or undefined while the
   *  first read travels. A server that predates the field reads ON, the
   *  default of migration 048. */
  function standingNow(): boolean | undefined {
    if (standing !== undefined) return standing;
    const read = storedSettingsCell(ctx);
    return read.state === 'ready' ? (read.value?.standingOrders ?? true) : undefined;
  }

  /** The STANDING ORDERS row (R1256), with its WEEKEND mark and the long
   *  sentence of what the autopilot does. */
  function standingRow(): string {
    const on = standingNow();
    return row({
      name: O.row_standing, what: O.row_standing_what, mark: tag(O.standing_mark), changed: on === false,
      control: on === undefined
        ? stateWord(O.standing_checking)
        : switchCtl(on, { act: 'standing' }, { label: O.row_standing, disabled: so.busy }),
      more: longLine(O.standing_long({ hours: ABSENCE_COVER_AFTER_HOURS })) + errNote(so.err),
    });
  }

  /** The end of the leave now, 0 for none, or undefined while the first
   *  read travels. A server that predates the field reads no leave. */
  function leaveNow(): number | undefined {
    if (leaveUntil !== undefined) return leaveUntil;
    const read = storedSettingsCell(ctx);
    return read.state === 'ready' ? (read.value?.onLeaveUntil ?? 0) : undefined;
  }

  /** The ON LEAVE row (R3283): OFF and one pill per span of LEAVE_DAYS.
   *  A leave that holds lights no pill, because it ends at an instant and
   *  not after a span; the line under the row names that instant. */
  function leaveRow(): string {
    const until = leaveNow();
    const now = Date.now() / 1000;
    const on = until !== undefined && until > now;
    const list = [{ id: '0', label: O.leave_off },
      ...LEAVE_DAYS.map((n) => ({ id: String(n), label: O.leave_days({ n }) }))];
    return row({
      name: O.row_leave, what: O.row_leave_what, changed: on,
      control: until === undefined
        ? stateWord(O.standing_checking)
        : pillGroup(list, on ? '' : '0', 'leave', { label: O.row_leave }),
      more: (on ? note(O.leave_until({ at: isoDateTime(until) })) : '')
        + longLine(O.leave_long) + errNote(lv.err),
    });
  }

  /** One press on a pill of the ON LEAVE row. It writes at once, and the
   *  row shows the instant the server answered. */
  function pickLeave(id: string): void {
    const days = Number(id);
    if (lv.busy || !(days === 0 || LEAVE_DAYS.includes(days))) return;
    runHeld(ctx, lv, 'sfx-menu-toggle', () => putLeave(days),
      (r) => { leaveUntil = r.onLeaveUntil; ctx.data.drop(SETTINGS_TYPED_CELL); });
  }

  /** One press on the standing-orders switch. It writes at once, and the
   *  row shows the value the server answered. */
  function toggleStanding(): void {
    const on = standingNow();
    if (on === undefined || so.busy) return;
    runHeld(ctx, so, 'sfx-menu-toggle', () => putStandingOrders(!on),
      (r) => { standing = r.standingOrders; ctx.data.drop(SETTINGS_TYPED_CELL); });
  }

  /**
   * The ADVISOR row (player-reach.md §4.7, R1566). It reads the switch the
   * account states on `/api/me`, AUTO resolved, as the in-game row reads
   * the empire's, so it is an ON/OFF switch. A server that predates the
   * field states none, and the row then stands out.
   */
  function advisorRow(): string {
    const me = ctx.me();
    if (me.advisorOn === undefined) return '';
    const err = errNote(adv.err);
    return row({
      name: O.row_advisor, what: S.advisor_desc,
      changed: me.advisor !== undefined && me.advisor !== 'auto',
      control: switchCtl(me.advisorOn, { act: 'advisor' }, { label: O.row_advisor, disabled: adv.busy }),
      more: err === '' ? undefined : err,
    });
  }

  /** One press on the ADVISOR row. It writes the account at once, and the
   *  row shows what the server answered. The world takes the switch at the
   *  next join. */
  function toggleAdvisor(): void {
    const me = ctx.me();
    if (me.advisorOn === undefined || adv.busy) return;
    runHeld(ctx, adv, 'sfx-menu-toggle', () => putAdvisor(!me.advisorOn),
      (r) => { ctx.setMe({ ...ctx.me(), advisor: r.advisor, advisorOn: r.advisorOn }); });
  }

  // --- INTERFACE: the language (R855) ---------------------------------------

  /** One press on a line of the language list. It opens the confirm strip,
   *  because a change reloads the page; the language in force closes it. */
  function pickLang(id: string): void {
    if (!langOffered(id)) return;
    pendingLang = id === s.lang ? null : id;
    ctx.play('sfx-menu-press');
    ctx.refresh();
  }

  /** RELOAD NOW. Every word on this page was written when the page loaded,
   *  so the press stores the choice, waits for the account push, and
   *  reloads. */
  function confirmLang(el: HTMLElement): void {
    const next = applyPress(s, ACT.langGo, el.dataset);
    pendingLang = null;
    if (next === null) {
      ctx.refresh();
      return;
    }
    write(next);
    ctx.play('sfx-menu-press');
    void flushSettings().then(() => { location.reload(); });
  }

  // --- ACCOUNT (R621) -------------------------------------------------------

  /**
   * One press on a fold button. A fold that closes over the delete puts
   * its arm gate at rest, so no armed control stands hidden. The button
   * takes the focus back after the rewrite, so a keyboard keeps its place.
   */
  function toggleFold(key: AccountFold): void {
    if (folds.has(key)) {
      folds.delete(key);
      if (key === 'erase') erase.clear();
    } else {
      folds.add(key);
    }
    refocus = key;
    ctx.play('sfx-menu-press');
    ctx.refresh();
  }

  /** One sign-in method row, and what stands under it. A method the
   *  account holds carries a quiet word and no control: there is nothing
   *  left to press. */
  function methodRow(name: string, label: string, desc: string, state: MethodState, under = ''): string {
    let control: string;
    if (state === 'link') {
      control = button(S.signin_link, { act: 'signin-link', id: name }, { variant: 'primary' });
    } else {
      const word = state === 'linked' ? S.signin_linked
        : state === 'checking' ? S.signin_checking : S.signin_off;
      control = stateWord(word, state === 'linked');
    }
    return `<div class="c-method" data-method="${esc(name)}">${row({ name: label, what: desc, control })}${under}</div>`;
  }

  /** One text box of a form, with what `text` holds for it. */
  function box(name: string, attrs: string, placeholder: string): string {
    return textBox(name, attrs, placeholder, text[name] ?? '');
  }

  /**
   * Runs one call of a form with the form in its busy state. A refusal is
   * put into words by `say`, and both outcomes play their cue and redraw.
   * A press while a call is in flight does nothing.
   */
  function submit(f: FormState, call: () => Promise<void>, say: (err: unknown) => string): void {
    if (f.busy) return;
    f.busy = true;
    f.err = '';
    ctx.refresh();
    void (async () => {
      try {
        await call();
        ctx.play('sfx-menu-signin');
      } catch (err) {
        f.err = say(err);
        ctx.play('sfx-menu-error');
      } finally {
        f.busy = false;
        ctx.refresh();
      }
    })();
  }

  /**
   * The inline form that adds the email method.
   *
   * It is a form under its own row and not a page of its own, because it
   * is one of three rows and the other two are one press. The call is the
   * same `POST /api/auth/register` the landing screen makes: with a session
   * in the cookie the meta server adds the password to the account that is
   * already there, so a guest keeps its captains and its Honour.
   */
  function emailForm(): string {
    return '<div class="c-form c-inset">'
      + box('signin-email', 'type="email" inputmode="email" autocomplete="username"', S.signin_email_box)
      + box('signin-password', 'type="password" autocomplete="new-password"', S.signin_password_box)
      + button(link.busy ? S.signin_busy : S.signin_continue, { act: 'signin-email-go' }, { variant: 'primary', disabled: link.busy })
      + '</div>' + errNote(link.err);
  }

  /**
   * The second section of the ACCOUNT page: the password change (R626).
   *
   * It is the same two boxes and one button the sign-in form uses, because
   * it asks the same kind of question. The `autocomplete` tokens are the
   * ones a password manager reads on a change form, so the manager offers
   * the stored secret in the first box and offers to save the new one.
   */
  function passwordForm(): string {
    return note(S.password_lede)
      + '<div class="c-form">'
      + box('pw-current', 'type="password" autocomplete="current-password"', S.password_current_box)
      + box('pw-next', 'type="password" autocomplete="new-password"', S.password_new_box)
      + button(pw.busy ? S.password_busy : S.password_continue, { act: 'password-go' }, { variant: 'primary', disabled: pw.busy })
      + '</div>' + errNote(pw.err)
      + (pwDone ? okNote(S.password_changed) : '');
  }

  /**
   * Replaces the password of the account this browser is signed in as.
   *
   * The refusals are the server's own sentences: the wrong current
   * password and a new one the rule refuses both arrive typed, and this
   * page invents neither. On success the two boxes go empty, because a
   * secret must not stand in a box after it was spent.
   */
  function changeThePassword(): void {
    if (pw.busy) return;
    pwDone = false;
    submit(pw, async () => {
      await changePassword(text['pw-current'] ?? '', text['pw-next'] ?? '');
      text['pw-current'] = '';
      text['pw-next'] = '';
      pwDone = true;
    }, messageOf);
  }

  /** Adds the email method to the account this browser is signed in as. */
  function linkEmail(): void {
    submit(link, async () => {
      await authRegister(text['signin-email'] ?? '', text['signin-password'] ?? '');
      // The account changed, so it is read again and never guessed:
      // `identities` is what the rows draw from. The cached copy the
      // shell refreshes on its own clock goes with it, or the next tick
      // would put the stale account back.
      ctx.data.drop('me');
      ctx.setMe(await getMe());
      linkOpen = false;
      text['signin-email'] = '';
      text['signin-password'] = '';
    }, (err) => {
      // A stale session (R2963): the SIGN IN AGAIN block says it, so the
      // form shows no second line.
      if (isReauth(err)) {
        reauth = true;
        return '';
      }
      // The one refusal this row can raise by itself: the account already
      // holds a password. Every other one is the server's own sentence.
      return err instanceof ApiError
        ? (err.code === CODE_ALREADY_LINKED ? S.signin_has_password : err.message)
        : String(err);
    });
  }

  /** SIGN IN AGAIN: signs out and opens the landing screen on this page
   *  (R2963). A sign-out that fails stays here and says why. */
  function signInOnceMore(): void {
    if (again.busy) return;
    again.busy = true;
    again.err = '';
    ctx.play('sfx-menu-press');
    ctx.refresh();
    signInAgain().catch((err: unknown) => {
      again.busy = false;
      again.err = messageOf(err);
      ctx.play('sfx-menu-error');
      ctx.refresh();
    });
  }

  /** The ACCOUNT page: the commander, the guest callout, the three folds
   *  and the THEMES link. */
  function accountBody(): string {
    const me = ctx.me();
    // A server that predates the field sends none, and the rows then offer
    // every method as a link. The server refuses what it must.
    const held = new Set(me.identities ?? []);
    // The method list is a read of this deployment and not of the account,
    // so it lives one time in the cache and every panel that needs it takes
    // the same cell.
    const offered = ctx.data.cell<string[]>('signin-methods', TTL_ON_OPEN, () => getSignInMethods());
    const list = SIGN_IN_METHODS.map(([name, label, desc]) => {
      if (held.has(name)) return methodRow(name, label, desc, 'linked');
      // The email method needs no configuration on the host, so it is
      // offered whatever the method list says or fails to say.
      if (name === 'email') return methodRow(name, label, desc, 'link', linkOpen ? emailForm() : '');
      if (offered.state === 'loading') return methodRow(name, label, desc, 'checking');
      const on = offered.state === 'ready' && (offered.value ?? []).includes(name);
      return methodRow(name, label, desc, on ? 'link' : 'off', ticketFail === name ? errNote(F.signin_state) : '');
    }).join('');
    // A guest reads a callout, because linking is not a convenience for a
    // guest: it is how the seat stops living in a cookie.
    const guest = me.account.kind === 'guest';
    return commanderCard(commander(true), { big: true, badge: badge(OPTIONS_MENU.accountBadgePx) })
      + (guest ? callout(S.account_lede_guest, '') : '')
      + fold({
        act: FOLD, key: 'signin', title: S.signin_section, open: folds.has('signin'),
        body: (guest ? '' : note(S.account_lede)) + list
          + (reauth ? reauthBlock(again.busy) + errNote(again.err) : ''),
      })
      + (held.has('email')
        ? fold({ act: FOLD, key: 'password', title: S.password_section, open: folds.has('password'), body: passwordForm() })
        : '')
      + fold({
        act: FOLD, key: 'erase', title: S.erase_section, open: folds.has('erase'), bad: true,
        body: erase.html(held.has('email'), again.busy) + (erase.reauth() ? errNote(again.err) : ''),
      })
      + linkRow(O.link_themes, O.link_themes_what, { act: 'go-mods' });
  }

  return {
    key: 'settings',

    openPage(p: PageKey): void {
      nav.ask(p);
    },

    openSection(word: string): void {
      nav.ask(word);
    },

    open(): void {
      // The in-game menu writes the same blob, so the store is the truth
      // and this panel re-reads it every time it comes up. An old arcade
      // mark scale of this device moves into the blob first (R1992).
      moveDeviceMarkScale();
      s = loadSettings();
      settled = s;
      pendingLang = null;
      alerts.open();
      // A guest's way to keep the seat is a sign-in method, and the guest
      // warning's REGISTER opens this tab, so a guest opens on the ACCOUNT
      // page with the fold open (R1565). A page the address named wins.
      const guest = ctx.me().account.kind === 'guest';
      if (guest) folds.add('signin');
      // A link that met `reauth_required` comes back to this page with the
      // code on the address (R2963), and the fold opens on SIGN IN AGAIN.
      if (takeReauth()) {
        reauth = true;
        folds.add('signin');
      }
      nav.open(guest ? 'account' : null);
    },

    close(): void {
      erase.clear();
      folds.clear();
      reauth = false;
      again.err = '';
      refocus = null;
      pendingLang = null;
      nav.close();
    },

    html(): string {
      return shell();
    },

    act(action: string, el: HTMLElement): void {
      if (shellPress(nav, ctx, SHELL, action, el, moved)) return;
      const key = el.dataset.key ?? '';
      switch (action) {
        case 'go-mods':
          ctx.play('sfx-menu-press');
          // The THEMES row means the THEMES section of MODDING (§16.16).
          ctx.go('mods', { section: 'themes' });
          return;
        case 'signin-link': {
          const provider = el.dataset.id ?? '';
          ctx.play('sfx-menu-press');
          if (provider === 'email') {
            linkOpen = !linkOpen;
            link.err = '';
            ctx.refresh();
            return;
          }
          // The start route sets the state cookie and redirects to the
          // provider, so this is a top-level navigation and never a fetch.
          // The callback comes back to `?tab=settings&section=account`,
          // which opens this page again with the row turned to LINKED. The
          // press carries no deep link, because the person pressed it here
          // (R1659).
          // The iOS app shell runs the flow in its sign-in sheet (R2927),
          // and the hub always holds a session, so it asks a start ticket
          // first (R2938). A failed ticket opens nothing and says so. A
          // stale session gets no ticket (R2963): SIGN IN AGAIN says so.
          ticketFail = '';
          void startOAuth(provider, { session: true }).then((ok) => {
            if (ok) return;
            ticketFail = provider;
            ctx.play('sfx-menu-error');
            ctx.refresh();
          }, (err: unknown) => {
            if (isReauth(err)) reauth = true;
            else ticketFail = provider;
            ctx.play('sfx-menu-error');
            ctx.refresh();
          });
          return;
        }
        case 'signin-email-go':
          linkEmail();
          return;
        case REAUTH_ACT:
          signInOnceMore();
          return;
        case 'password-go':
          changeThePassword();
          return;
        case FOLD: {
          const f = foldOf(key);
          if (f) toggleFold(f);
          return;
        }
        case 'standing':
          toggleStanding();
          return;
        case 'leave':
          pickLeave(el.dataset.id ?? '');
          return;
        case 'advisor':
          toggleAdvisor();
          return;
        case ACT.lang:
          pickLang(el.dataset.id ?? '');
          return;
        case ACT.langNo:
          pendingLang = null;
          ctx.play('sfx-menu-back');
          ctx.refresh();
          return;
        case ACT.langGo:
          confirmLang(el);
          return;
        case ACT.theme: {
          // The UI theme (R1776) goes through the theme state, so the link
          // follows the pick; the hub's look listener wears it and redraws.
          const pick = stepUiTheme(s, heldKeys(ctx.me()), Number(el.dataset.id));
          if (pick === null) return;
          s = setThemePick('ui', pick);
          settled = s;
          ctx.play('sfx-menu-press');
          ctx.refresh();
          return;
        }
        case ACT.sw:
        case ACT.chart:
        case ACT.scale:
        case ACT.render:
        case ACT.chatDesk:
        case ACT.casinoDesk:
        case ACT.sheets:
        case ACT.tabLabels: {
          // The one press rule of the shared rows (`rows.ts`). The store's
          // write applies a new UI scale to this page at once.
          const next = applyPress(s, action, el.dataset);
          if (next === null) return;
          write(next);
          ctx.play(action === ACT.sw ? 'sfx-menu-toggle' : 'sfx-menu-press');
          ctx.refresh();
          return;
        }
        default:
          // One of the four mounted cards takes the press; each answers
          // false for an action that is not its own.
          erase.act(action, el) || blocks.act(action, el)
            || hours.act(action, el) || alerts.act(action, el);
      }
    },

    /**
     * The inputs. A button acts through the shell's press router, and a
     * range and a text box are not buttons, so they are wired here. The
     * pane is rewritten whenever the markup changes, so every value is
     * written back from the panel's own state on every pass.
     */
    wire(root: HTMLElement): void {
      alerts.wire(root);
      erase.wire(root);
      nav.scroll(root);
      if (refocus !== null) {
        root.querySelector<HTMLElement>(`button[data-act="${FOLD}"][data-key="${refocus}"]`)?.focus();
        refocus = null;
      }
      // The markup carries no level (`kit.ts`), so the ranges, the figures
      // and the bars take theirs here.
      syncRangeMarks(root, s);
      for (const key of RANGE_KEYS) {
        const el = root.querySelector<HTMLInputElement>(`input.o-range[data-key="${key}"]`);
        if (!el || !wireOnce(el)) continue;
        el.addEventListener('input', () => {
          const v = rangeValue(key, el.value);
          if (v === s[key]) return;
          write({ ...s, [key]: v }, false);
          syncRangeMarks(root, s);
          // One cue for each step the drag crosses, and not one for each
          // event: the step index is the whole test.
          const step = rangeStep(key, v);
          if (step !== lastTick[key]) {
            lastTick[key] = step;
            ctx.play('sfx-menu-tick');
          }
        });
        // The drag ended: the summary takes the new level.
        el.addEventListener('change', () => {
          settled = s;
          ctx.refresh();
        });
      }
      // The boxes of the two forms of the ACCOUNT page: the inline email
      // form under the EMAIL row, and the password change. Both keep their
      // text in `text` and answer Enter with their own call, so one loop
      // wires them and the prefix says which call that is. The shell keeps
      // the caret in a box, because it does not preventDefault an INPUT
      // press.
      for (const el of root.querySelectorAll<HTMLInputElement>(
        'input[data-in^="signin-"], input[data-in^="pw-"]')) {
        if (!wireOnce(el)) continue;
        const name = el.dataset.in ?? '';
        const go = name.startsWith('pw-') ? changeThePassword : linkEmail;
        el.addEventListener('input', () => { text[name] = el.value; });
        el.addEventListener('keydown', (ev) => {
          if (!matches(KEYS.hub.submit, ev)) return;
          text[name] = el.value;
          go();
        });
      }
    },
  };
}
